import React, { useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import InputLabel from '@material-ui/core/InputLabel';
import QuarterSelect from './QuarterSelect';
import YearSelect from './YearSelect';
import CoverageCodeSelect from './CoverageCodeSelect';
import InsurerSelect from './InsurerSelect';
import CountySelect from './CountySelect';
import { SEARCH_QUERY_PARAMS } from 'constants/querystring';
import { addNotification } from 'state/actions';
import { NOTIFICATION_LEVELS } from 'constants/application';
import { FormControl, FormControlLabel, RadioGroup, Radio } from '@material-ui/core';

const CriterionItem = ({ children }) => (
  <Grid item xs={12} sm={6}>
    {children}
  </Grid>
);

const emptyCriteria = {
  [SEARCH_QUERY_PARAMS.CoverageCode]: [],
  [SEARCH_QUERY_PARAMS.Insurer]: [],
  [SEARCH_QUERY_PARAMS.County]: [],
};

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
    top: '-2rem',
  },
  labelAdjustment: {
    position: 'relative',
    top: '1.5rem',
  },
  groupByContainer: {
    position: 'relative',
    top: '2.5rem',
  },
  groupByLabel: {
    position: 'relative',
    top: '0.25rem',
  },
}));

const defaultEndQuarter = () => {
  const currentMonth = new Date().getMonth();
  return currentMonth < 3 ? 1 : currentMonth < 6 ? 2 : currentMonth < 9 ? 3 : 4;
};

const defaultEndYear = () => {
  const currentYear = new Date().getFullYear();
  return currentYear;
};

const SearchCriteria = ({
  onSearch,
  updateQsOnSearch = true,
  startQuarterOptions: startQuarterOverrides = {},
  startYearOptions: startYearOverrides = {},
  hideStartDate = false,
  endQuarterOptions: endQuarterOverrides = {},
  endYearOptions: endYearOverrides = {},
  hideEndDate = false,
  coverageCodeOptions: coverageCodeOverrides = {},
  hideCoverageCodes = false,
  hideInsurers = false,
  hideCounties = false,
  disableSearch = false,
  allowMultipleCounty = true,
  allowMultipleInsurer = true,
  allowMultipleCoverageCode = true,
  showGroupByQuarterAndYear = false,
  showAllQuarters = false,
}) => {
  const [criteria, setCriteria] = React.useState(emptyCriteria);
  const classes = useStyles();
  const [searchParams, setSearchParams] = useSearchParams();
  const _quarter = searchParams.get(SEARCH_QUERY_PARAMS.Quarter);
  const _year = searchParams.get(SEARCH_QUERY_PARAMS.Year);
  const _startQuarter = searchParams.get(SEARCH_QUERY_PARAMS.StartQuarter);
  const _startYear = searchParams.get(SEARCH_QUERY_PARAMS.StartYear);
  const _endQuarter = searchParams.get(SEARCH_QUERY_PARAMS.EndQuarter);
  const _endYear = searchParams.get(SEARCH_QUERY_PARAMS.EndYear);
  const _coverageCodes = searchParams.get(SEARCH_QUERY_PARAMS.CoverageCode);
  const _counties = searchParams.get(SEARCH_QUERY_PARAMS.County);
  const _insurers = searchParams.get(SEARCH_QUERY_PARAMS.Insurer);
  const _groupBy = searchParams.get(SEARCH_QUERY_PARAMS.GroupBy);

  const startQuarterOptions = {
    id: SEARCH_QUERY_PARAMS.StartQuarter,
    name: SEARCH_QUERY_PARAMS.StartQuarter,
    ...startQuarterOverrides,
  };

  const startYearOptions = {
    id: SEARCH_QUERY_PARAMS.StartYear,
    name: SEARCH_QUERY_PARAMS.StartYear,
    ...startYearOverrides,
  };

  const endQuarterOptions = {
    id: SEARCH_QUERY_PARAMS.EndQuarter,
    name: SEARCH_QUERY_PARAMS.EndQuarter,
    ...endQuarterOverrides,
  };

  const endYearOptions = {
    id: SEARCH_QUERY_PARAMS.EndYear,
    name: SEARCH_QUERY_PARAMS.EndYear,
    ...endYearOverrides,
  };

  const coverageCodeOptions = {
    name: SEARCH_QUERY_PARAMS.CoverageCode,
    id: SEARCH_QUERY_PARAMS.CoverageCode,
    multiple: allowMultipleCoverageCode,
    ...coverageCodeOverrides,
  };

  const countyOptions = {
    name: SEARCH_QUERY_PARAMS.County,
    id: SEARCH_QUERY_PARAMS.County,
    multiple: allowMultipleCounty,
  };

  const insurerOptions = {
    name: SEARCH_QUERY_PARAMS.Insurer,
    id: SEARCH_QUERY_PARAMS.Insurer,
    multiple: allowMultipleInsurer,
  };

  useEffect(() => {
    const initCriteria = {
      [SEARCH_QUERY_PARAMS.Quarter]: _quarter || 1,
      [SEARCH_QUERY_PARAMS.Year]: _year || 2013,
      [SEARCH_QUERY_PARAMS.StartQuarter]: _groupBy === 'Q' ? _startQuarter || 1 : 1,
      [SEARCH_QUERY_PARAMS.StartYear]: _startYear || 2013,
      [SEARCH_QUERY_PARAMS.EndQuarter]: _groupBy === 'Q' ? _endQuarter || defaultEndQuarter() : 4,
      [SEARCH_QUERY_PARAMS.EndYear]: _endYear || defaultEndYear(),
      [SEARCH_QUERY_PARAMS.CoverageCode]: _coverageCodes ? _coverageCodes.split(',') : [],
      [SEARCH_QUERY_PARAMS.Insurer]: _insurers ? _insurers.split(',') : [],
      [SEARCH_QUERY_PARAMS.County]: _counties ? _counties.split(',') : [],
      [SEARCH_QUERY_PARAMS.GroupBy]: _groupBy || 'Q',
    };
    setCriteria(initCriteria);

    if (onSearch) {
      onSearch(initCriteria);
    }
  }, [
    _quarter,
    _year,
    _startQuarter,
    _startYear,
    _endQuarter,
    _endYear,
    _coverageCodes,
    _insurers,
    _counties,
    onSearch,
    _groupBy,
  ]);

  const handleChange = (event) => {
    setCriteria({ ...criteria, [event.target.name]: event.target.value });
    if (hideEndDate) {
      if (event.target.name === 'startQuarter') {
        setCriteria({ ...criteria, quarter: event.target.value });
      }
      if (event.target.name === 'startYear') {
        setCriteria({ ...criteria, year: event.target.value });
      }
    }
  };

  const handleSelectChange = (event = { name: '', value: [] }) => {
    setCriteria({
      ...criteria,
      [event.name]: event.value.reduce((acc, { id }) => {
        acc.push(id);
        return acc;
      }, []),
    });
  };

  const handleSearch = useCallback(() => {
    const {
      [SEARCH_QUERY_PARAMS.County]: qsCounties = [],
      [SEARCH_QUERY_PARAMS.CoverageCode]: qsCoverageCodes = [],
      [SEARCH_QUERY_PARAMS.Insurer]: qsInsurers = [],
      ...rest
    } = criteria;
    const qsCriteria = {
      [SEARCH_QUERY_PARAMS.County]: qsCounties.join(','),
      [SEARCH_QUERY_PARAMS.CoverageCode]: qsCoverageCodes.join(','),
      [SEARCH_QUERY_PARAMS.Insurer]: qsInsurers.join(','),
      ...rest,
    };
    setSearchParams(qsCriteria);
    if (
      !allowMultipleCounty &&
      (criteria.counties.length === 0 || Number(criteria.counties[0]) === 0)
    ) {
      addNotification('County is required', NOTIFICATION_LEVELS.ERROR);
    }
    if (
      !allowMultipleInsurer &&
      (criteria.insurers.length === 0 || Number(criteria.insurers[0]) === 0)
    ) {
      addNotification('Insurer is required', NOTIFICATION_LEVELS.ERROR);
    }
    if (
      !allowMultipleCoverageCode &&
      (criteria.coverageCodes.length === 0 || Number(criteria.coverageCodes[0]) === 0)
    ) {
      addNotification('Coverage Code is required', NOTIFICATION_LEVELS.ERROR);
    }
  }, [
    criteria,
    setSearchParams,
    allowMultipleCounty,
    allowMultipleInsurer,
    allowMultipleCoverageCode,
  ]);

  const {
    [SEARCH_QUERY_PARAMS.Quarter]: quarter,
    [SEARCH_QUERY_PARAMS.Year]: year,
    [SEARCH_QUERY_PARAMS.StartQuarter]: startQuarter,
    [SEARCH_QUERY_PARAMS.StartYear]: startYear,
    [SEARCH_QUERY_PARAMS.EndQuarter]: endQuarter,
    [SEARCH_QUERY_PARAMS.EndYear]: endYear,
    [SEARCH_QUERY_PARAMS.CoverageCode]: coverageCodes,
    [SEARCH_QUERY_PARAMS.Insurer]: insurers,
    [SEARCH_QUERY_PARAMS.County]: counties,
    [SEARCH_QUERY_PARAMS.GroupBy]: groupBy,
  } = criteria;

  return (
    <>
      <Grid item xs={12} className={classes.root}>
        <Grid container spacing={2}>
          {showGroupByQuarterAndYear ? (
            <>
              <Grid item xs={12} className={classes.groupByContainer}>
                <FormControl component="fieldset">
                  <InputLabel shrink className={classes.groupByLabel}>
                    Group By
                  </InputLabel>
                  <RadioGroup
                    aria-label="Group By"
                    name="groupBy"
                    value={groupBy}
                    onChange={handleChange}
                    row
                  >
                    <FormControlLabel
                      value="Q"
                      control={<Radio color="primary" />}
                      label="Quarter and Year"
                      checked={groupBy === 'Q'}
                    />
                    <FormControlLabel
                      value="Y"
                      control={<Radio color="primary" />}
                      label="Year"
                      checked={groupBy === 'Y'}
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </>
          ) : null}

          {!hideStartDate ? (
            <CriterionItem>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <InputLabel shrink className={classes.labelAdjustment}>
                    {hideEndDate ? 'Quarter' : groupBy === 'Q' ? 'Start Quarter' : 'Start Year'}
                  </InputLabel>
                </Grid>
                <Grid item xs={6}>
                  <InputLabel shrink className={classes.labelAdjustment}>
                    {groupBy === 'Q' ? 'Year' : null}
                  </InputLabel>
                </Grid>
                {groupBy === 'Q' ? (
                  <Grid item xs={6}>
                    <QuarterSelect
                      onChange={handleChange}
                      defaultValue={hideEndDate ? quarter : startQuarter}
                      showAllQuarters={showAllQuarters}
                      {...startQuarterOptions}
                    />
                  </Grid>
                ) : null}

                <Grid item xs={6}>
                  <YearSelect
                    onChange={handleChange}
                    defaultValue={hideEndDate ? year : startYear}
                    {...startYearOptions}
                  />
                </Grid>
              </Grid>
            </CriterionItem>
          ) : null}

          {!hideEndDate ? (
            <CriterionItem>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <InputLabel shrink className={classes.labelAdjustment}>
                    {groupBy === 'Q' ? 'End Quarter' : 'End Year'}
                  </InputLabel>
                </Grid>
                <Grid item xs={6}>
                  <InputLabel shrink className={classes.labelAdjustment}>
                    {groupBy === 'Q' ? 'Year' : null}
                  </InputLabel>
                </Grid>
                {groupBy === 'Q' ? (
                  <Grid item xs={6}>
                    <QuarterSelect
                      onChange={handleChange}
                      defaultValue={endQuarter}
                      {...endQuarterOptions}
                    />
                  </Grid>
                ) : null}
                <Grid item xs={6}>
                  <YearSelect onChange={handleChange} defaultValue={endYear} {...endYearOptions} />
                </Grid>
              </Grid>
            </CriterionItem>
          ) : null}

          <Grid item xs={12}>
            <Grid container spacing={2}>
              {!hideCoverageCodes ? (
                <Grid item xs={6}>
                  <CoverageCodeSelect
                    onChange={handleSelectChange}
                    defaultValue={coverageCodes || []}
                    {...coverageCodeOptions}
                  />
                </Grid>
              ) : null}

              {!hideCounties ? (
                <Grid item xs={6}>
                  <CountySelect
                    onChange={handleSelectChange}
                    defaultValue={counties || []}
                    {...countyOptions}
                  />
                </Grid>
              ) : null}
            </Grid>
          </Grid>
          {!hideInsurers ? (
            <Grid item xs={12}>
              <InsurerSelect
                onChange={handleSelectChange}
                defaultValue={insurers || []}
                {...insurerOptions}
              />
            </Grid>
          ) : null}

          <Grid item xs={12} className="print-search-criteria">
            <Box textAlign="right">
              <Button
                onClick={handleSearch}
                variant="contained"
                disableElevation
                disabled={disableSearch}
                color="primary"
              >
                Search
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default SearchCriteria;
